
import { Component, Prop, Vue } from "vue-property-decorator";
import vSelect3 from "vselect3";
import { Getter, namespace } from "vuex-class";
import { ActionPlanResultLabelTypes, EventTask, Task, TaskTypes } from "@/store/tasks/types";
import { CustomFieldTypes, CustomFieldExtendedDataIconDictionary, CustomFieldExtendedDataTypes, CustomFieldExtendedData, CustomFieldExtendedDataEmitValue, ActionPlanValue, ActionPlanValueOption } from "@/store/custom-fields/types";
import { truncateString } from "@/filters";

const Tasks = namespace("tasks");
/**
 * CustomDefinedFieldExtendedDataActionPlanResult.vue
 *
 * This component renders a dynamic select input for action plan results.
 * It allows users to select an action plan result from predefined options.
 */
@Component({
	components: {
		"v-select-3": vSelect3,
	},
	filters: { truncateString },
})
export default class CustomDefinedFieldExtendedDataActionPlanResult extends Vue {
	public $refs!: {
		dropdown: vSelect3;
	};

	@Prop({ required: false }) public data?: CustomFieldExtendedData;
	@Prop({ type: Number, default: 170 }) public readonly optionLengthMax: number;
	@Prop({ required: false }) public icon?: string; //Optional icon class to override the default.
	@Prop({ required: false }) public valuesInUse?: string[];

	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Tasks.Getter("getTasks") eventTasks: EventTask[];

	private cachedTaskResults: Task[] = [];
	private dropdownOpen: boolean = false;
	private readonly defaultPlaceholder = "Select an action plan result";

	private selectedValue: ActionPlanValue | ActionPlanValue[] | null = null;

	private manualOverridePrefix: string = "Manually overridden value";
	private listSeparator: string = " - ";

	private processedValuesInUse: string[] = [];

	private get isAutoPopulateEnabled(): boolean {
		return !!this.getFeature(["CustomFields", "NotificationTemplates", "ExtendedData", "ActionPlanResult", "AutoPopulate"]);
	}

	private mounted(): void {
		/**
		 * If a default action plan is specified in the custom field options,
		 * automatically selects and emits the corresponding completed task result.
		 * This is done on mount to display custom field labels detailing that the value
		 * was auto-populated from action plan steps.
		 */
		if (this.data &&
			this.data.customField &&
			this.data.customField.options &&
			this.data.customField.options.dataFromActionPlans &&
			this.data.customField.options.dataFromActionPlans.labelIds &&
			this.isAutoPopulateEnabled)
		{
			const selectedTask = this.retrieveSelectedTaskResult();

			// If a matching completed task is found, set it as the selected value.
			// And emit it to the parent component.
			if (selectedTask) {
				this.selectedValue = {
					value: this.getTaskResultString(selectedTask),
					label: this.getTaskResultString(selectedTask),
					taskText: this.getTaskTitle(selectedTask),
				};

				this.emitValue(this.selectedValue);
			}
		}

		if (this.valuesInUse && this.valuesInUse.length > 0) {
			this.processedValuesInUse = this.valuesInUse.map((val: string) => {
				val.trim().replace(`${this.manualOverridePrefix}${this.listSeparator}`, "")

				// Remove leading list separator if present
				if (val.startsWith(this.listSeparator)) {
					val = val.replace(this.listSeparator, "");
				}

				return val;
			});

			if (this.isMultiple) {
				this.selectedValue = this.processedValuesInUse.map((value: string) => {
					return {
						value: value,
						label: value,
						taskText: "",
					};
				}); 
			} else {
				const val = this.processedValuesInUse[0];
				this.selectedValue = {
					value: val,
					label: val,
					taskText: "",
				};
			}

			this.emitValue(this.selectedValue);
		}
	}

	private retrieveSelectedTaskResult(): EventTask | null {
		// Check if there are any completed action plans that match the specified label IDs.
		// Also check if the auto-populate feature is enabled.
		// If it is, return the first matching completed task.
		if (this.data &&
			this.data.customField &&
			this.data.customField.options &&
			this.data.customField.options.dataFromActionPlans &&
			this.data.customField.options.dataFromActionPlans.labelIds &&
			this.isAutoPopulateEnabled)
		{
			let defaultActionPlan: number = null;

			this.data.customField.options.dataFromActionPlans.labelIds.forEach((planId) => {
				const task = this.getSupportedCompletedTasks.find((t) => t.associatedLabelIds.includes(planId));
				// If a task is found, set it as the default action plan.
				// If multiple tasks are found, the first one will be used.
				if (task) {
					defaultActionPlan = planId;
				}
			});

			return this.getSupportedCompletedTasks.find(
				(task: EventTask) => task.associatedLabelIds.includes(defaultActionPlan)
			);
		}
	}

	/**
	 * Shows or hides the dropdown based on the state.
	 * @param state The state to set for the dropdown visibility.
	 */
	private showDropdown(state): void {
		// Show the dropdown when the icon is clicked.
		this.dropdownOpen = state;
		setTimeout(() => {
			if (this.$refs.dropdown) {
				this.$refs.dropdown.open = state;
			}
		}, 100);
	}

	private getTaskResultString(task: Task): string {
		const SitRepText = "Situation Report Downloaded";
		const MassNotificationText = "Notification Sent";
		const BasicText = "Was Completed";

		try {
			let taskContent = "";
			switch (task.taskTypeID) {
				case TaskTypes.URL:
					taskContent = `Visited ${task.taskData?.toString() ?? "URL"}`;
					break;
				case TaskTypes.SitRep:
					taskContent = SitRepText;
					break;
				case TaskTypes.Basic:
					taskContent = BasicText;
					break;
				case TaskTypes.MassNotification:
					taskContent = MassNotificationText;
					break;
				default:
					taskContent = task.result ?? BasicText;
			}

			if (this.includeTaskTitle) {
				taskContent = this.getTaskTitle(task) + ` - ` + taskContent;
			}

			return taskContent;
		} catch {
			return BasicText;
		}
	}

	private getTaskValueString(task: { value: string; label: string; taskText: string }): string {
		if (task) {
			return this.data.customField.options.dataFromActionPlans?.includeTaskTitle
							? task.taskText + ` - ${task.value}`
							: task.value
		}
		return "";
	}

	private getTaskTitle(task: Task): string {
		switch (task.taskTypeID) {
			case TaskTypes.EventOutcome:
				return "Event Outcome";
			default:
				return task.taskText;
		}
	}

	/**
	 * Emits the selected option's value when user selects an option.
	 * @param selectedOption The selected option object from v-select-3.
	 */
	private async handleSelected(selectedOption: { value: string; label: string; taskText: string }): Promise<void> {
		this.emitValue(selectedOption);
		this.showDropdown(false);
	}

	/**
	 * Emits the selected value to the parent component.
	 * @param selectedOption The selected option object from v-select-3.
	 */
	private emitValue(selectedOption: ActionPlanValue | ActionPlanValue[]): void {
		// Emit the selected value to the parent component.
		if (selectedOption == null) {
			// If no option is selected, emit an empty value.
			this.$emit("input", {
				value: null,
				updateMessage: null,
			} as CustomFieldExtendedDataEmitValue);
			return;
		}

		let valueToEmit: string = "";

		if (Array.isArray(selectedOption)) {
			valueToEmit = this.getTaskValueString(selectedOption[selectedOption.length - 1]);
		} else {
			valueToEmit = selectedOption.value;
		}

		const message: string = this.generateEmitMessage(selectedOption);

		this.$emit("input", {
			value: valueToEmit,
			updateMessage: message,
		} as CustomFieldExtendedDataEmitValue);
	}

	private generateEmitMessage(selectOption: ActionPlanValue | ActionPlanValue[]): string {
		if (Array.isArray(selectOption)) {
			const labels = selectOption.map(opt => (opt.taskText || opt.value));
			const { valuesFromActionPlan, valuesFromManualOverride }: ActionPlanResultLabelTypes 
				= this.groupLabelsForMessage(labels);

			let message = "";
			message += valuesFromActionPlan.length > 0
				? `Values pulled from Action Plan Steps: ${valuesFromActionPlan.join(", ")}. `
				: "";
			message += valuesFromManualOverride.length > 0
				? `Values manually overridden from action plan: ${valuesFromManualOverride.join(", ")}.`
				: "";
			return message;
		}

		const label = selectOption.taskText || selectOption.value;
		if (label.includes(this.manualOverridePrefix)) {
			return "Value manually overridden from action plan.";
		}

		// If no previous values exist or label not in use, return standard message
		const pulledFromActionPlanMessage = `Value pulled from Action Plan Step: ${label}`;
		if (!this.processedValuesInUse || this.processedValuesInUse?.length === 0 || !this.processedValuesInUse.includes(label)) {
			return pulledFromActionPlanMessage;
		}

		// If the value was previously used, check if it matches the current action plan result.
		const actionPlanResult = this.retrieveSelectedTaskResult();
		if (!actionPlanResult) {
			return pulledFromActionPlanMessage;
		}

		// Check if the selected label matches the current action plan result.
		const actionComparator = this.getTaskResultString(actionPlanResult) ?? this.getTaskTitle(actionPlanResult);
		if (actionComparator == label) {
			return pulledFromActionPlanMessage;
		}

		this.$emit("show-action-plan-alert", true);
		return `Action plan may have changed since previous email. Please review options and confirm: ${label}`;
	}

	private groupLabelsForMessage(labels: string[]): ActionPlanResultLabelTypes {
		const labelsObject: ActionPlanResultLabelTypes = {
			valuesFromActionPlan: [],
			valuesFromManualOverride: [],
		};

		labels.forEach((label) => {
			if (label.includes(this.manualOverridePrefix)) {
				labelsObject.valuesFromManualOverride.push(label);
			} else {
				labelsObject.valuesFromActionPlan.push(label);
			} 
		});
		return labelsObject;
	}

	private onCreateOption(option: string): ActionPlanValue {
		// Allow users to create custom options only if auto-populate is enabled.
		this.$emit('option:created', option);
		return {
			value: option,
			label: option,
			taskText: this.manualOverridePrefix,
		};
	}

	/**
	 * Returns the completed tasks from the event tasks.
	 * Filters tasks to only include those that are completed.
	 */
	private get getSupportedCompletedTasks(): EventTask[] {
		// todo: Filter tasks to only include supported task types.
		return this.eventTasks.filter((task) => task.completed);
	}

	private get options(): ActionPlanValueOption[] {
		// Cache the 'getCompletedTasks' so that we don't auto-update the list every 5 seconds
		if (this.cachedTaskResults.length === 0) {
			this.cachedTaskResults = [...this.getSupportedCompletedTasks];
		}

		var completedTaskOptions = (this.cachedTaskResults ?? [])
			.map((task: Task) => {
				const resultString = this.getTaskResultString(task);
				return {
					value: resultString,
					label: resultString,
					isCompletedTask: true,
					eventTaskId: task.eventTaskID,
					taskText: task.taskText,
				};
			})
			.filter(Boolean);

		var customFieldOptions: ActionPlanValueOption[] = [];
		if (this.data.customField?.options?.data) {
			customFieldOptions = this.data.customField.options.data
				.map((option) => {
					return {
						value: option,
						label: option,
						isCompletedTask: false,
						taskText: '',
					};
				})
				.filter(Boolean);
		}

		return [...customFieldOptions, ...completedTaskOptions];
	}

	private get dropdownPlaceholder(): string {
		if (this.data.customField?.placeholder && this.data.customField?.placeholder !== "") {
			return this.data.customField.placeholder;
		}
		return this.defaultPlaceholder;
	}

	private get customFieldIsList(): boolean {
		return !!this.data.customField && this.data.customField?.customFieldTypeId === CustomFieldTypes.List;
	}

	private get actionPlanResultIcon(): string {
		return CustomFieldExtendedDataIconDictionary[CustomFieldExtendedDataTypes.ActionPlanResult]?.icon ?? "fas fa-clipboard";
	}

	private get isMultiple(): boolean {
		return this.data.customField?.options?.allowMultiple ?? false;
	}

	private get includeTaskTitle(): boolean {
		return this.data?.customField?.options?.dataFromActionPlans?.includeTaskTitle ?? false;
	}
}
